<script>
  import PlatformHeader from "@/components/molecules/PlatformHeader.vue";
  import CredentialField from "@/components/molecules/CredentialField.vue";
  import TextIcon from "@/components/molecules/TextIcon.vue";
  import UserNameTimeStamp from "@/components/molecules/userNameTimeStamp.vue";
  import Paragraph from "../atoms/Paragraph.vue";
  import { isEmail, isRequired } from "@/utils/validators";

  export default {
    name: "PlatformCard",
    components: {
      PlatformHeader,
      CredentialField,
      TextIcon,
      UserNameTimeStamp,
      Paragraph,
    },
    props: {
      platform: Object,
      credentials: Object,
      user: Object,
      initiallyOpen: { type: Boolean, default: false },
      isCreationMode: { type: Boolean, default: true },
      history: { type: Object, default: () => ({ active: [], inActive: [] }) },
    },

    data() {
      return {
        fields: {
          email: this.credentials.email || "",
          password: this.credentials.password || "",
          userName: this.credentials.userName || "",
          apiKey: this.credentials.apiKey || "",
        },

        errors: { email: "", password: "", userName: "", apiKey: "" },

        fieldValidators: {
          email: [{ rule: isEmail, message: "Ingresa un correo electrónico válido" }],
          password: [{ rule: isRequired, message: "Ingresa una contraseña" }],
          userName: [{ rule: isRequired, message: "Ingresa un userName" }],
          apiKey: [{ rule: isRequired, message: "Ingresa tu API Key" }],
        },

        open: this.initiallyOpen,
        stylesNew: {
          label: { outline: "1px solid #969696" },
          container: { border: "1px solid #A0A0A0" },
        },
      };
    },

    watch: {
      cardIsValid(val) {
        this.$emit("card-validation-change", { platform: this.platform.name, valid: val });
      },
      hasApiKeyField(newVal) {
        if (!newVal) {
          this.errors.apiKey = "";
        }
      },
    },

    computed: {
      cardIsValid() {
        return Object.values(this.errors).every((e) => !e);
      },
      isNewStyles() {
        return this.isCreationMode ? this.stylesNew : {};
      },
      hasApiKeyField() {
        return this.platform?.name === "Chaturbate";
      },
    },

    methods: {
      validateField(field) {
        if (field === "apiKey" && !this.hasApiKeyField) {
          this.errors.apiKey = "";
          return;
        }

        const value = this.fields[field];
        const validators = this.fieldValidators[field] || [];
        const errorObj = validators.find((v) => !v.rule(value));

        this.$set(this.errors, field, errorObj ? errorObj.message : "");
      },

      validateAll() {
        Object.keys(this.fieldValidators).forEach(this.validateField);
      },

      onFieldInput(field, { value }) {
        this.$set(this.fields, field, value);
        this.validateField(field);

        this.$emit("onChange", {
          input: { id: field, value },
          platform: this.platform,
        });
      },
      trySave(type) {
        this.validateAll();

        if (!this.cardIsValid) {
          this.$toast.error("Corrige los campos antes de guardar");
          return;
        }

        this.$emit("onSave", { type, platform: this.platform, user: this.user });
      },
    },
    created() {
      this.validateAll();
      this.$emit("card-validation-change", {
        platform: this.platform.name,
        valid: this.cardIsValid,
      });
    },
  };
</script>

<template>
  <article class="platform-card" :style="isNewStyles.container">
    <div class="platform-card__wrapper">
      <Platform-header :name="platform.name" :icon="platform.logo" :open="open" @toggle="open = !open" :showBtn="isCreationMode" />

      <section class="platform-card__body">
        <CredentialField
          type="email"
          idInput="email"
          label="Correo:"
          :styles="isNewStyles"
          :value="fields.email"
          :invalid="!!errors.email"
          :error-message="errors.email"
          :showBtns="!isCreationMode"
          placeholder="Models@models1a.com"
          @input="onFieldInput('email', $event)"
          @onSave="trySave('email')"
        />

        <CredentialField
          type="password"
          idInput="password"
          label="Contraseña:"
          :styles="isNewStyles"
          :value="fields.password"
          :invalid="!!errors.password"
          :error-message="errors.password"
          :showBtns="!isCreationMode"
          @input="onFieldInput('password', $event)"
          @onSave="trySave('password')"
        />
        <CredentialField
          idInput="userName"
          label="Username:"
          :styles="isNewStyles"
          :value="fields.userName"
          :invalid="!!errors.userName"
          :error-message="errors.userName"
          :showBtns="!isCreationMode"
          @input="onFieldInput('userName', $event)"
          @onSave="trySave('userName')"
        />
        <CredentialField
          v-if="hasApiKeyField"
          type="text"
          idInput="apiKey"
          label="API Key:"
          :styles="isNewStyles"
          :value="fields.apiKey"
          :invalid="!!errors.apiKey"
          :error-message="errors.apiKey"
          :showBtns="!isCreationMode"
          placeholder="sk_live_xxx..."
          @input="onFieldInput('apiKey', $event)"
          @onSave="trySave('apiKey')"
        />
      </section>
    </div>

    <transition name="platform-card__fade">
      <div class="platform-card__history" v-if="open">
        <hr />

        <TextIcon label="Historial de usernames" variant="primary" :styles="{ label: { fontSize: '16px' } }" icon="historyIcon" />

        <UserNameTimeStamp v-if="history.active.length" :user="history.active[0].createdBy" :userName="history.active[0].userName" :label="history.active[0].createdAt" :showPill="true" />

        <p v-if="history.inActive.length" class="platform-card__subtitle">Anteriores</p>

        <UserNameTimeStamp v-for="(item, idx) in history.inActive" :key="`inactive-${idx}-${item._id}`" :user="item.createdBy" :userName="item.userName" :label="item.createdAt" />
      </div>
    </transition>
  </article>
</template>

<style lang="scss" scoped>
  .platform-card {
    @include Flex(column, center, unset);
    width: 100%;
    gap: 16px;
    background: #f8f8f8;
    padding: 20px;
    border-radius: 8px;

    &__wrapper {
      @include Flex(column, center, unset);
      gap: 17px;
      width: 100%;
      max-width: 473px;
    }

    &__body {
      gap: 6px;
    }

    &__history {
      @include Flex(column, center, unset);
      width: 100%;
      gap: 16px;
    }

    &__subtitle {
      font-family: $newFont_regular;
      color: #000;
      font-size: 16px;
    }

    &__fade-enter-active,
    &__fade-leave-active {
      transition: opacity 0.15s ease;
    }
    &__fade-enter,
    &__fade-leave-to {
      opacity: 0;
    }
  }
</style>
